'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { Offer, OffersOffer } from '@/services/dataTypes'
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getMerchantHref } from '@/constants/hooks'
import { apiOfferDetails } from '@/apis/offers'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import ProductCommentModal from './ProductCommentModal'
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react'

interface Props {
    product: OffersOffer
    companyId: string
    awaited_p_id?: string
    mer_slug_type: string
    mer_slug: string
    domain: string
    merchantLogo?: string | null
    ads_campaign: boolean
}

function isValidImageUrl(src?: string | null): boolean {
    if (!src || typeof src !== 'string') return false
    const s = src.trim()
    if (!s || s === 'null' || s === 'undefined' || s === '/null' || s === 'none') return false
    if (s.includes('awltovhc.com') || s.includes('emjcd.com') || s.includes('anrdoezrs.net') || s.includes('linksynergy.com') || s.includes('clickserve.')) {
        return false
    }
    return true
}

const OffersListView = ({ product, companyId, mer_slug_type, mer_slug, domain, merchantLogo: storeMerchantLogo }: Props) => {
    const cleanDomain = (!domain || domain === 'no domain in cookie' || domain.startsWith('localhost')) ? '' : domain
    const [p_data, setP_data] = useState<Offer | null>(null)
    const [showModal, setShowModal] = useState(false)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [showTerms, setShowTerms] = useState(false)
    const [imgError, setImgError] = useState(false)

    const rawOfferImg =
        (isValidImageUrl(product?.offer?.product_image) ? product?.offer?.product_image : null) ||
        (isValidImageUrl((product as any)?.product_image) ? (product as any)?.product_image : null) ||
        (isValidImageUrl((product?.offer as any)?.image) ? (product?.offer as any)?.image : null) ||
        (isValidImageUrl(product?.offer?.banner_image) ? product?.offer?.banner_image : null) ||
        (isValidImageUrl((product as any)?.banner_image) ? (product as any)?.banner_image : null) ||
        ''

    const merchantLogo =
        (isValidImageUrl(product?.merchant?.merchant_logo) ? product?.merchant?.merchant_logo : null) ||
        (isValidImageUrl(product?.offer?.merchant?.merchant_logo) ? product?.offer?.merchant?.merchant_logo : null) ||
        (isValidImageUrl(storeMerchantLogo) ? storeMerchantLogo : null) ||
        ''

    useEffect(() => {
        setImgError(false)
    }, [rawOfferImg, merchantLogo])

    const imageSrc = imgError ? (rawOfferImg ? merchantLogo : '') : rawOfferImg || merchantLogo
    const hasImage = Boolean(imageSrc)

    const merchantHref = getMerchantHref(product.merchant, mer_slug, mer_slug_type)
    const isCoupon = !!product?.offer?.coupon_code
    const isProduct = product?.offer?.offer_type?.name === 'product'

    // Compute Discount Box labels
    const getDiscountInfo = () => {
        const title = (product?.offer?.offer_title || '').toLowerCase()
        const detail = (product?.offer?.offer_detail || '').toLowerCase()
        const text = `${title} ${detail}`

        const originalPrice = product?.offer?.original_price ? parseFloat(product.offer.original_price) : 0
        const salePrice = product?.offer?.sale_price ? parseFloat(product.offer.sale_price) : 0
        const discountPercent = originalPrice > 0 && salePrice > 0
            ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
            : null

        if (discountPercent && discountPercent > 0) {
            return { value: `${discountPercent}%`, label: 'OFF' }
        }

        const percentMatch = text.match(/(\d{1,3})%\s*(off)?/)
        if (percentMatch) {
            if (text.includes('up to')) {
                return { value: `UP TO ${percentMatch[1]}%`, label: 'OFF' }
            }
            if (text.includes('extra')) {
                return { value: `${percentMatch[1]}%`, label: 'EXTRA OFF' }
            }
            return { value: `${percentMatch[1]}%`, label: 'OFF' }
        }

        const dollarMatch = text.match(/(\$\d+)\s*off/)
        if (dollarMatch) {
            return { value: dollarMatch[1], label: 'OFF' }
        }

        if (text.includes('free shipping') || text.includes('free delivery')) {
            return { value: 'FREE', label: 'SHIPPING' }
        }

        if (text.includes('clearance')) {
            return { value: 'CLEARANCE', label: 'SAVE' }
        }

        if (isCoupon) {
            return { value: 'CODE', label: 'COUPON' }
        }
        return { value: 'DEAL', label: 'SAVE' }
    }

    // Compute Tag Badge
    const getBadgeInfo = () => {
        const text = `${product?.offer?.offer_title || ''} ${product?.offer?.offer_detail || ''}`.toLowerCase()

        if (isCoupon) {
            if (text.includes('exclusive')) {
                return {
                    text: 'Exclusive',
                    style: 'bg-rose-50 text-rose-700 border border-rose-100',
                }
            }
            return {
                text: 'Verified Coupon',
                style: 'bg-emerald-50 text-[#00875a] border border-emerald-100',
            }
        }

        if (text.includes('free shipping')) {
            return {
                text: 'Free Shipping',
                style: 'bg-sky-50 text-sky-700 border border-sky-100',
            }
        }

        if (text.includes('clearance')) {
            return {
                text: 'Clearance Deal',
                style: 'bg-amber-50 text-amber-800 border border-amber-100',
            }
        }

        if (text.includes('sale') || text.includes('discount')) {
            return {
                text: 'Sale Deal',
                style: 'bg-emerald-50 text-[#00875a] border border-emerald-100',
            }
        }

        return {
            text: 'Verified Deal',
            style: 'bg-emerald-50 text-[#00875a] border border-emerald-100',
        }
    }

    const discountInfo = getDiscountInfo()
    const badgeInfo = getBadgeInfo()

    const verifiedDate = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })

    const expiryDateText = product?.offer?.life_time === 1 || !product?.offer?.end_date
        ? 'Ongoing'
        : `Expires ${new Date(product.offer.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`

    const handleViewDetail = async () => {
        if (loadingDetail) return
        setLoadingDetail(true)
        try {
            const res = await apiOfferDetails(product?.offer?.unique_id, companyId)
            const data = res?.data ?? (res as any)?.offer ?? null
            setP_data(data || product.offer)
            setShowModal(true)
        } catch {
            setP_data(product.offer)
            setShowModal(true)
        } finally {
            setLoadingDetail(false)
        }
    }

    return (
        <>
            {showModal && p_data && (
                <ProductCommentModal
                    product={p_data}
                    merchantName={product.merchant?.merchant_name || 'Brand'}
                    merchantLogo={getBaseImageUrl(domain, product.merchant?.merchant_logo || '', '')}
                    domain={domain}
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    productImg={rawOfferImg || p_data?.product_image || product?.offer?.product_image || (p_data as any)?.offer?.product_image || p_data?.banner_image || product?.offer?.banner_image || (product as any)?.product_image}
                    companyId={companyId}
                />
            )}

            <div className="group relative bg-white rounded-2xl border border-slate-200/90 hover:border-[#00d69f]/60 hover:shadow-md transition-all duration-200 p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                    {/* Left side: Product/Deal image if available, otherwise discount badge box */}
                    {hasImage ? (
                        <div
                            onClick={handleViewDetail}
                            className="w-24 sm:w-28 h-22 sm:h-24 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-center p-2 shrink-0 cursor-pointer overflow-hidden group-hover:border-[#00d69f]/40 transition-colors"
                        >
                            <Image
                                src={getBaseImageUrl(cleanDomain, imageSrc, '')}
                                alt={product?.offer?.offer_title || 'Deal'}
                                width={128}
                                height={128}
                                unoptimized
                                onError={() => setImgError(true)}
                                className="object-contain max-h-18 max-w-18 sm:max-h-20 sm:max-w-20 group-hover:scale-105 transition-transform"
                            />
                        </div>
                    ) : (
                        <div className="w-24 sm:w-28 h-22 sm:h-24 rounded-xl border border-dashed border-emerald-300/80 bg-[#f4fbf8] flex flex-col items-center justify-center text-center p-2 shrink-0">
                            <span className="text-xl sm:text-2xl font-black text-[#00a87a] leading-none">
                                {discountInfo.value}
                            </span>
                            <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mt-1">
                                {discountInfo.label}
                            </span>
                        </div>
                    )}

                    {/* Middle details */}
                    <div className="flex-1 min-w-0">
                        {/* Badges row */}
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold rounded-full ${badgeInfo.style}`}>
                                <CheckCircle size={10} /> {badgeInfo.text}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded">
                                {isCoupon ? 'Code' : 'Deal'}
                            </span>
                        </div>

                        {/* Title */}
                        <h3
                            onClick={handleViewDetail}
                            className="text-sm sm:text-base font-bold text-[#071822] hover:text-[#00a87a] transition-colors leading-snug cursor-pointer line-clamp-2"
                        >
                            {discardHTMLTags(product?.offer?.offer_title?.replaceAll('_', ' '))}
                        </h3>

                        {/* Description */}
                        {product?.offer?.offer_detail && (
                            <p className="text-xs text-slate-800 line-clamp-2 mt-1 leading-relaxed font-normal">
                                {discardHTMLTags(product.offer.offer_detail)}
                            </p>
                        )}

                        {/* Price (if product) */}
                        {isProduct && product?.offer?.sale_price && parseFloat(product.offer.sale_price) > 0 && (
                            <div className="flex items-baseline gap-2 mt-1.5">
                                <span className="text-base font-black text-slate-900">
                                    {getCurrencySymbol(product.offer.currency)}{parseFloat(product.offer.sale_price).toFixed(2)}
                                </span>
                                {product?.offer?.original_price && parseFloat(product.offer.original_price) > 0 && (
                                    <span className="text-xs text-slate-400 line-through">
                                        {getCurrencySymbol(product.offer.currency)}{parseFloat(product.offer.original_price).toFixed(2)}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Metadata row */}
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-slate-400 font-medium">
                            <span>Verified {verifiedDate}</span>
                            <span>·</span>
                            <span>{expiryDateText}</span>
                            <span>·</span>
                            <button
                                onClick={() => setShowTerms(!showTerms)}
                                className="inline-flex items-center gap-0.5 text-slate-600 hover:text-[#00a87a] font-semibold underline underline-offset-2 cursor-pointer"
                            >
                                <span>View Details</span>
                                {showTerms ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </button>
                        </div>
                    </div>

                    {/* Right CTA Button */}
                    <div className="shrink-0 w-full sm:w-auto pt-2 sm:pt-0">
                        <OfferOutUrl
                            unique_id={product.offer.unique_id}
                            outUrl={product.offer.url}
                            merchantHref={merchantHref}
                            domain={domain}
                            customClass="w-full sm:w-auto block"
                        >
                            <div
                                onClick={handleViewDetail}
                                className={`w-full sm:w-auto px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm text-center shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer min-w-[110px] ${isCoupon
                                    ? 'bg-[#c8411b] hover:bg-[#b03514] text-white'
                                    : 'bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] text-white'
                                    }`}
                            >
                                {isCoupon ? 'Get Code' : isProduct ? 'Buy Now' : 'Get Deal'}
                            </div>
                        </OfferOutUrl>
                    </div>
                </div>

                {/* Expandable Terms accordion */}
                {showTerms && (
                    <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-800 space-y-1.5 animate-fadeIn">
                        <p className="font-semibold text-slate-800">Offer Terms & Details:</p>
                        <p className="text-slate-800 leading-relaxed">
                            {discardHTMLTags(product?.offer?.offer_detail) || 'Apply coupon code at checkout on the official store website. Limited to one offer per transaction. Cannot be combined with other offers unless specified. Subject to store availability and terms.'}
                        </p>
                        {product?.offer?.end_date && (
                            <p className="text-slate-500 text-[11px]">Valid until: {product.offer.end_date}</p>
                        )}
                    </div>
                )}
            </div>
        </>
    )
}

export default OffersListView
